/**
 * The per-tenant equipment catalog (`equipment_types`, API migration 0246).
 *
 * Typed to the ACTUAL payload of `GET /api/v1/equipment-types`, which is
 * enveloped and camelCase throughout:
 *
 *     { success: true, data: [{ id, companyId, name, isStandard, isActive,
 *                              createdAt, updatedAt }] }
 *
 * The list is active-only unless the request carries `?include_inactive=true`.
 *
 * THE CATALOG IS AUTHORITATIVE, THE TEXT COLUMN IS A CACHE
 * -------------------------------------------------------
 * Unlike `service_types` — whose consumers store display strings in
 * `jobs.services_required` jsonb, so a rename there is not a migration — this
 * catalog owns `equipment.type` outright. API migration 0334 installed two
 * triggers:
 *
 *   trg_equipment_derive_type          the FK decides the text, on every write
 *   trg_equipment_types_cascade_name   a rename reaches every row using it
 *
 * So renaming a type here DOES rewrite the equipment that references it, and
 * the column comment in 0334 says `equipment.type` is "NOT independently
 * writable". Do not reconcile the two client-side; the database already did.
 *
 * WHAT A STANDARD ROW CAN DO: NOTHING
 * -----------------------------------
 * `is_standard` is checked in PATCH and DELETE *before* the body is parsed, so
 * a standard row refuses a rename, a deactivate and a delete identically with
 * 403. This differs from `service_types`, where a standard row is renameable
 * and deactivatable and only refuses delete — copying that gate here ships two
 * controls that fail on click.
 */

/** One row of a tenant's equipment catalog. */
export interface EquipmentType {
  id: string;
  companyId: string;
  name: string;
  /**
   * Seeded by the platform — ten rows per company, from API migration 0246.
   * NOT renameable, NOT deactivatable, NOT deletable. All three answer 403.
   */
  isStandard: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Fields a client may write.
 *
 * Both keys are optional and may be sent together. camelCase on the wire —
 * PATCH reads `body.isActive`, and a snake_case `is_active` is simply absent,
 * so the row comes back unchanged with a 200 and nothing reports a failure.
 */
export interface EquipmentTypePayload {
  name?: string;
  isActive?: boolean;
}

/**
 * Normalize one row from the wire.
 *
 * The live payload is camelCase, but reads stay tolerant of snake_case per the
 * house rule — an `isActive` that arrives as `is_active` and reads `undefined`
 * would drop every type from the picker, which looks exactly like "this tenant
 * has no equipment types" rather than like a bug.
 *
 * `isActive` defaults to true and `isStandard` to false: a row the server sent
 * us is a row that exists, and the safe failure is offering a type that should
 * have been withdrawn rather than withdrawing one that should have been
 * offered. `isStandard` defaulting false is the same reasoning pointed the
 * other way — an unreadable flag shows controls that 403 on click, which is
 * recoverable, where the inverse silently strips a custom row of every control
 * and reads as the API having lost the type.
 */
export function toEquipmentType(raw: any): EquipmentType | null {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw.id;
  const name = raw.name;
  if (typeof id !== 'string' || typeof name !== 'string') return null;

  return {
    id,
    companyId: raw.companyId ?? raw.company_id ?? '',
    name,
    isStandard: (raw.isStandard ?? raw.is_standard) === true,
    isActive: (raw.isActive ?? raw.is_active) !== false,
    createdAt: raw.createdAt ?? raw.created_at ?? '',
    updatedAt: raw.updatedAt ?? raw.updated_at ?? '',
  };
}

/**
 * Catalog order: standard first, then alphabetical within each half.
 *
 * This mirrors `ORDER BY is_standard DESC, name ASC` in the API handler and the
 * sort the Add Equipment dropdown already applies, so the management page and
 * the picker agree about position. There is no `display_order` column on this
 * table — unlike `service_types` — so there is nothing to reorder and no
 * move-up control.
 */
export function compareEquipmentTypes(a: EquipmentType, b: EquipmentType): number {
  if (a.isStandard !== b.isStandard) return a.isStandard ? -1 : 1;
  return a.name.localeCompare(b.name);
}
